import {
  useCallback,
  useRef,
} from 'react';
import type {
  ChangeEventHandler,
  KeyboardEventHandler,
  ReactElement,
  ReactNode,
} from 'react';

import { useTheme } from 'styled-components';
import { ClearButton } from '../ClearButton';
import {
  Input,
  StyledInputButton,
} from '../Input';
import type {
  InputProps,
} from '../Input';

export type AdditionalSearchInputProps = {
  /**
   * Значение поля
   */
  value: string;
  /**
   * Выключено ли поле
   */
  disabled?: boolean;

  /**
  * Обработчик изменения инпута
  * @param {Event} event - событие
  */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
  * Обработчик изменения значения поля (в т.ч. при нажатии на кнопку сброса)
  * @param {string} newValue - новое значение поля
  */
  onValueChange?: (nextValue: string) => void;

  /**
   * Обработчик нажатия на кнопку сброса
   */
  handleClear?: () => void;
  /**
  * Обработчик нажатия на кнопку поиска
  * @param {string} searchValue - текущее значение поля поиска
  */
  handleSearch?: (searchValue: string) => void;
};

export type SearchInputProps = InputProps<'input'> & AdditionalSearchInputProps;

export function SearchInput({
  handleSearch: handleSearchProp = undefined,
  handleClear: handleClearProp = undefined,
  onChange: onChangeProp = undefined,
  onValueChange = undefined,
  value,
  disabled = false,
  ...rest
}: SearchInputProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const { input } = useTheme();

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    if (onChangeProp) {
      onChangeProp(event);
    }

    if (onValueChange) {
      onValueChange(event.target.value);
    }
  }, [
    onChangeProp,
    onValueChange,
  ]);

  const handleClear = useCallback(() => {
    if (handleClearProp) {
      handleClearProp();
    }

    if (onValueChange) {
      onValueChange('');
    }

    inputRef.current?.focus();

    if (handleSearchProp) {
      handleSearchProp('');
    }
  }, [
    onValueChange,
    handleClearProp,
    handleSearchProp,
  ]);

  const handleSearch = useCallback(() => {
    if (handleSearchProp) {
      handleSearchProp(value);
    }
  }, [
    value,
    handleSearchProp,
  ]);

  const handleKeyPress = useCallback<KeyboardEventHandler>((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      handleSearch();
    }
  }, [
    handleSearch,
  ]);

  const renderClearButton = useCallback((inputProps: InputProps<'input'>): ReactNode => {
    const {
      small,
    } = inputProps;

    return (
      <StyledInputButton
        type="button"
        disabled={disabled}
        $small={small}
        onClick={handleClear}
        key="clear"
      >
        <ClearButton />
      </StyledInputButton>
    );
  }, [
    disabled,
    handleClear,
  ]);

  const renderSearchButton = useCallback((inputProps: InputProps<'input'>): ReactNode => {
    const {
      small,
    } = inputProps;

    return (
      <StyledInputButton
        type="button"
        disabled={disabled}
        $small={small}
        onClick={() => inputRef.current?.focus()}
        key="search"
      >
        {input.SEARCH_ICON}
      </StyledInputButton>
    );
  }, [
    disabled,
    input.SEARCH_ICON,
  ]);

  const leftButtons: ((inputProps: InputProps<'input'>) => ReactNode)[] = [];
  const rightButtons: ((inputProps: InputProps<'input'>) => ReactNode)[] = [];

  if (value) {
    rightButtons.push(renderClearButton);
  }

  leftButtons.push(renderSearchButton);

  return (
    <Input
      {...rest}
      disabled={disabled}
      value={value}
      onChange={onChange}
      onKeyPress={handleKeyPress}
      leftButtons={leftButtons}
      rightButtons={rightButtons}
      ref={inputRef}
    />
  );
}
